import type { RuleProperties } from 'json-rules-engine';
import type { ObjectId } from 'mongodb';

import type { Meta } from '../Entities';
import type { IsoDateString, WithObjectIdsAsStrings } from '../Helpers';
import type { AppliedDataFilter } from '../ListFilters';
import type { ProgramType } from '../ProgramTypes';

import type { BatchDocument } from './Batches';
import type { ClawbackCategory, ProjectClawbackRuleNames } from './Clawbacks';
import { type PayoutEvent, PayoutEventType } from './Events';
import { type PayeeDocument, PayeeType, type ProjectDocument } from './Payees';

/**
 * Business timezone for payout pause-expiration date math. Pause windows are tracked as
 * date-only fields (`pauseExpirationDate`), so the calendar-day boundary must be anchored to
 * a single business timezone rather than UTC — otherwise transactions created on the same
 * business day but straddling UTC midnight get expirations a day apart.
 */
export const PAYOUT_PAUSE_TIME_ZONE = 'America/Los_Angeles';

export interface ProjectTransactionRejection {
  date: Date;
  reason: string;
  rejectedBy: { id: string; name: string };
}

export enum ProjectTransactionStatus {
  open = 'open', // not in a batch
  paid = 'paid',
  pending = 'pending', // added to batch
  approved = 'approved', // approved to be paid
  migrated = 'migrated', // migrated from past events
  historical = 'historical', // historical estimate
  adjustment = 'adjustment', // historical adjustment
  paused = 'paused', // paused by accounting
  // cancelled?
  // error?
}

export const ProjectTransactionStatusMap: Record<ProjectTransactionStatus, string> = {
  [ProjectTransactionStatus.open]: 'Open',
  [ProjectTransactionStatus.paid]: 'Paid',
  [ProjectTransactionStatus.pending]: 'Pending',
  [ProjectTransactionStatus.approved]: 'Approved',
  [ProjectTransactionStatus.migrated]: 'Migrated',
  [ProjectTransactionStatus.historical]: 'Historical Est.',
  [ProjectTransactionStatus.adjustment]: 'Historical Adjustment',
  [ProjectTransactionStatus.paused]: 'Paused',
};

export interface ProjectTransactionHistory {
  date: Date;
  user: { id: string; name: string };
  summary?: string;
  changes?: Record<string, any>;
}

export interface PayoutCalculationDetails {
  remainingProjectFunds?: number;
  currentTotalCost?: number;
  asString: string;
  variables?: Record<string, number>;
}

export enum PayoutsCollection {
  payees = 'payees',
  projects = 'projects',
  projectClawbacks = 'projectClawbacks',
  projectTransactions = 'projectTransactions',
  batches = 'batches',
  batchSummaries = 'batchSummaries',
  projectTransactionsView = 'projectTransactionsView',
  projectDeductions = 'projectDeductions',
}
// Views should not be updated directly, include all views here
export const immutablePayoutsCollections: PayoutsCollection[] = [PayoutsCollection.projectTransactionsView];

export interface PayoutRuleProperties extends RuleProperties {
  appliesToEvents: Array<PayoutEvent>;
}

export enum ProjectTransactionDeleteReason {
  planChange = 'planChange',
  deleteHistoricOrgMaterialsPayoutAndCreateNewTransaction = 'deleteHistoricOrgMaterialsPayoutAndCreateNewTransaction',
}

export enum PayeePayoutEvent {
  equipment = 'equipment',
  shipping = 'shipping',
  manualAdjustment = 'manualAdjustment',
}

export const PayeePayoutEventToEventTypeMap: Record<PayeePayoutEvent, PayoutEventType> = {
  [PayeePayoutEvent.equipment]: PayoutEventType.deduction,
  [PayeePayoutEvent.shipping]: PayoutEventType.deduction,
  [PayeePayoutEvent.manualAdjustment]: PayoutEventType.credit,
};

export enum GeneralLedgerCategory {
  thirdParty = 'thirdParty',
  general = 'general',
  safeHarbor = 'safeHarbor',
}

export enum PayoutTransactionLevel {
  project = 'project',
  payee = 'payee',
}

export const PayeeTypeFilterMap: Record<PayeeType, string> = {
  [PayeeType.organization]: 'Organization',
  [PayeeType.vendor]: 'Vendor',
};

export const PayeePayoutEventMap: Record<PayeePayoutEvent, string> = {
  [PayeePayoutEvent.equipment]: 'Equipment',
  [PayeePayoutEvent.shipping]: 'Shipping',
  [PayeePayoutEvent.manualAdjustment]: 'Manual Adjustment',
};

export const TransactionLevelFilterMap: Record<PayoutTransactionLevel, string> = {
  [PayoutTransactionLevel.project]: 'Account',
  [PayoutTransactionLevel.payee]: 'Organization',
};

export const GeneralLedgerCategoryMap: Record<GeneralLedgerCategory, string> = {
  [GeneralLedgerCategory.thirdParty]: 'Third Party',
  [GeneralLedgerCategory.general]: 'General',
  [GeneralLedgerCategory.safeHarbor]: 'Safe Harbor',
};

export interface BasePayoutTransaction {
  id?: string;
  payeeId: ObjectId;
  batchId?: ObjectId;
  payeeType: PayeeType;
  status: ProjectTransactionStatus;
  pauseExpirationDate?: IsoDateString;
  pauseReason?: string;
  amount: number;
  prePayment: boolean;
  eventType: PayoutEventType;
  eventDate: Date;
  description?: string;
  rejections?: Array<ProjectTransactionRejection>;
  meta?: Meta;
  originRule?: string;
  attributes?: Record<string, any>;
  references?: Record<string, string>;
  history?: Array<ProjectTransactionHistory>;
  isManual?: boolean;
}

export interface ProjectTransactionDocument extends BasePayoutTransaction {
  transactionLevel: PayoutTransactionLevel.project;
  projectId: ObjectId;
  projectExternalId: string;
  programType: ProgramType;
  planId?: ObjectId;
  calculation?: PayoutCalculationDetails;
  event: PayoutEvent;
  /** @deprecated */
  clawbackCategory?: ClawbackCategory;
  clawbackRuleName?: ProjectClawbackRuleNames;
}

export interface PayeeTransactionDocument extends BasePayoutTransaction {
  transactionLevel: PayoutTransactionLevel.payee;
  payeeExternalId: string;
  event: PayeePayoutEvent;
  generalLedgerCategory: GeneralLedgerCategory;
  webhookRef?: string;
}

export type PayoutTransactionDocument = ProjectTransactionDocument | PayeeTransactionDocument;

export type CreatePayeeTransactionPayload = Pick<
  PayeeTransactionDocument,
  'event' | 'generalLedgerCategory' | 'amount'
> & {
  payeeExternalId: string;
  description: string;
  eventDate?: Date;
  references?: Record<string, string>;
  /**
   * The options below are for system-generated payee transactions. The manual POST route's body
   * schema is closed (`additionalProperties: false`), so they can never arrive from a request.
   */
  status?: ProjectTransactionStatus;
  pauseExpirationDate?: IsoDateString;
  pauseReason?: string;
  isManual?: boolean;
  historySummary?: string;
};

export interface ProjectTransactionsView extends ProjectTransactionDocument {
  transactionId: ObjectId;
  batch?: BatchDocument;
  payee: Omit<PayeeDocument, 'planHistory'>;
  project: ProjectDocument;
}

/**
 * One row of projectTransactionsView, at either transaction level.
 *
 * The view is a single heterogeneous collection: project rows carry a project, payee rows do not,
 * and read paths query it generically. Modelling that as a union would force every consumer to
 * narrow before touching a field, for no safety a plain optional does not already give. Fields that
 * belong to only one level are optional here for that reason.
 *
 **/
export interface PayoutTransactionsView extends BasePayoutTransaction {
  transactionId: ObjectId;
  transactionLevel: PayoutTransactionLevel;
  batch?: BatchDocument;
  payee: Omit<PayeeDocument, 'planHistory'>;
  event: PayoutEvent | PayeePayoutEvent;

  project?: ProjectDocument;
  projectId?: ObjectId;
  projectExternalId?: string;
  programType?: ProgramType;
  planId?: ObjectId;
  calculation?: PayoutCalculationDetails;
  /** @deprecated */
  clawbackCategory?: ClawbackCategory;
  clawbackRuleName?: ProjectClawbackRuleNames;

  generalLedgerCategory?: GeneralLedgerCategory;
  source?: 'manual' | 'webhook';
  webhookRef?: string;
}

/**
 * These properties are in finco DB
 */
export type AdditionalPayoutData = {
  payoutOnHold?: boolean;
  organizationPlanId?: string;
  organizationPlanName?: string;
};

export type ProjectTransactionSort =
  | 'batchDateAsc'
  | 'batchDateDesc'
  | 'payeeNameAsc'
  | 'payeeNameDesc'
  | 'amountAsc'
  | 'amountDesc'
  | 'eventDateAsc'
  | 'eventDateDesc'
  | 'projectNameAsc'
  | 'projectNameDesc'
  | 'eventAsc'
  | 'eventDesc'
  | 'statusAsc'
  | 'statusDesc'
  | 'projectExternalIdDesc'
  | 'projectExternalIdAsc'
  | 'pausedExpirationDateAsc'
  | 'pausedExpirationDateDesc';

export type ProjectTransactionsViewWithFincoData = ProjectTransactionsView & AdditionalPayoutData;

/**
 * A row of any transaction level, enriched with finco data. The list endpoints read the view
 * generically, so they return this rather than the project-only shape: with the organization payout
 * transactions flag on, the same query yields payee rows that carry no project fields at all.
 */
export type PayoutTransactionsViewWithFincoData = PayoutTransactionsView & AdditionalPayoutData;

export interface getProjectTransactionsQuery {
  batchId?: string;
  batchType?: string;
  batchDate?: string;
  payeeId?: string;
  payeeExternalId?: string | Record<'$ne', string> | Record<'$nin', string[]>;
  payeeType?: PayeeType | Record<'$ne', PayeeType>;
  projectId?: string;
  projectExternalId?: string; // Finco AccountId
  projectOwnerExternalIds?: string[];
  eventType?: PayoutEventType;
  status?: ProjectTransactionStatus;
  programType?: string;
  transactionLevel?: PayoutTransactionLevel | 'all';
  advancedFilters?: AppliedDataFilter[];
  search?: string;
}

export interface getProjectTransactionsQueryOptions {
  sort?: ProjectTransactionSort;
  format?: 'csv' | 'json';
  page?: string;
  limit?: string;
}

export type ProjectWithPaymentPlanInfo = ProjectDocument & {
  primaryPayee: PayeeDocument;
};

export type PaginationElement = {
  count: number;
  page: number;
  limit: number;
  totalAmount?: number;
  totalPayees?: number;
};

export type CreateProjectTransactionPayload = Partial<
  WithObjectIdsAsStrings<
    Pick<
      ProjectTransactionDocument,
      | 'projectId'
      | 'projectExternalId'
      | 'event'
      | 'description'
      | 'amount'
      | 'payeeId'
      | 'payeeType'
      | 'clawbackRuleName'
      | 'isManual'
    > & { batchDate?: IsoDateString; historicalAdjustment?: boolean; eventDate?: Date }
  >
>;

export interface ProjectTransactionsSummary {
  directPay: number;
  payoutAmountToDate: number;
  projectTotalCost: number;
  remainingPayout: number;
  paymentSchedule: {
    amount: number;
    event: string;
    expectedAmount: number;
    paymentPercent: number;
  }[];
}
